import { css } from '@emotion/react';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { MentorshipProgramSession } from 'redux/schemas/models/mentoring-program-sessions';
import { getMentorshipProgramSession } from 'redux/selectors/mentorship-program-sessions';
import { useAppDispatch } from 'redux/store';
import { getSingleMentoringProgramSession, resetMentoringProgramSessionsList } from 'redux/actions/mentoring-program-sessions';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import PulseCheckForm from './pulse-check-form';
import PulseCheckConfirmationModal from './pulse-check-confirmation-modal';

type PulseCheckModalProps = {
  sessionId: number;
  setShowModal: (showModal: boolean) => void;
  setShouldShowPulseCheckCTA: (shouldShowPulseCheckCTA: boolean) => void;
};

const modalStyles = css`
  &.bs4-modal {
    .bs4-modal-dialog {
      .bs4-modal-body {
        padding: 0;
        overflow: hidden;
      }
    }
  }
`;

const PulseCheckModal = ({ sessionId, setShowModal, setShouldShowPulseCheckCTA }: PulseCheckModalProps) => {
  const dispatch = useAppDispatch();
  const { injectServices } = useContext(AngularContext);
  const [$state, ConfettiAnimation] = injectServices([
    '$state',
    'ConfettiAnimation',
  ]);
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { id: programId, mentorshipProgramEnrollment: enrollmentId } = mentoringProgram || {};
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.SESSION_PULSE_CHECK;
  const session: MentorshipProgramSession = useSelector(
    getMentorshipProgramSession(sessionId),
  );
  const [showPulseCheck, setShowPulseCheck] = useState(false);
  const [isPulseCheckResponded, setIsPulseCheckResponded] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (sessionId && !session) {
      dispatch(getSingleMentoringProgramSession({ programId, sessionId }));
    }
  }, [dispatch, programId, session, sessionId]);

  useEffect(() => {
    if (session) {
      const { pulseCheckResponses, mentorshipProgramConnection } =
        session || {};
      const { mentorshipProgramConnectionMembers } =
        mentorshipProgramConnection || {};
      const isConnectionMember = mentorshipProgramConnectionMembers.some(
        (member) => member.mentorshipProgramEnrollment.id === enrollmentId
      );
      if (!isConnectionMember) {
        $state.go('mentoring-program-root', { programId });
      } else {
        if (pulseCheckResponses?.[enrollmentId]) {
          setIsPulseCheckResponded(true);
        } else {
          setShowPulseCheck(true);
        }
      }
    }
  }, [enrollmentId, session]);

  const resetSessionIdParam = () => {
    const params = { sessionId: undefined };
    $state.go($state.current, params, { notify: false });
  };

  const onCloseModal = () => {
    resetSessionIdParam();
    setShowModal(false);
  };

  const onSubmit = () => {
    setShowConfirmation(true);
    ConfettiAnimation?.showConfetti();
    dispatch(resetMentoringProgramSessionsList({ resetList: true }));
    setShouldShowPulseCheckCTA(false);
  };

  if (showConfirmation) {
    return (
      <PulseCheckConfirmationModal
        description={translationBase.CONFIRMATION.SUCCESS()}
        onClose={onCloseModal}
      />
    );
  }

  if (isPulseCheckResponded) {
    return (
      <PulseCheckConfirmationModal
        description={translationBase.CONFIRMATION.ALREADY_RESPONDED()}
        onClose={onCloseModal}
      />
    );
  }

  if (showPulseCheck) {
    return (
      <NvModal
        show
        width={800}
        height='unset'
        doubleModal
        type={ModalType.FIXED}
        header={translationBase.TITLE()}
        body={(
          <PulseCheckForm
            programId={programId}
            enrollmentId={enrollmentId}
            session={session}
            onSubmit={onSubmit}
            onClose={onCloseModal}
          />
        )}
        modalStyles={modalStyles}
        onClose={onCloseModal}
      />
    );
  }

  return null;
};

export default PulseCheckModal;
